import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import React, { useCallback, useMemo, useRef } from 'react';

import { StructureTableProps } from './StructureTable.props';
import { ColumnDef } from './StructureTable.types';
import { StructureTableHead } from './StructureTableHead';
import { StructureTableRow } from './StructureTableRow';
import { useContainerWidth } from './useContainerWidth';
import { elasticLastColumnSx, stickyActionsTableSx } from '../../../configs/tableStyles';
import { useFreezeColumnWidths } from '../../../hooks/useFreezeColumnWidths';

// Fixed width of the sticky actions column.
const ACTIONS_COL_WIDTH = 70;
const DEFAULT_FLEX_MIN_WIDTH = 100;

interface ColLayout {
  widths: string[];
  minTableWidth: number;
}

// Non-frozen layout: fill 100% of the container, distributing slack by each
// column's `flex` weight and honouring `minWidth` floors (the classic list-table
// sizing). Once every column carries an explicit pixel width the table switches
// to the frozen fixed-pixel model instead. Consumers without a resize handler
// (onColumnResize) never freeze, so they keep this fill-to-container layout.
const buildColLayout = (cols: ColumnDef[], containerWidth: number | null): ColLayout => {
  let fixedTotal = ACTIONS_COL_WIDTH;
  let flexTotal = 0;
  let minTableWidth = ACTIONS_COL_WIDTH;
  for (const c of cols) {
    if (c.width != null) {
      fixedTotal += c.width;
      minTableWidth += c.width;
    } else {
      flexTotal += c.flex ?? 1;
      minTableWidth += c.minWidth ?? DEFAULT_FLEX_MIN_WIDTH;
    }
  }

  const floorOf = (c: ColumnDef) => c.minWidth ?? DEFAULT_FLEX_MIN_WIDTH;

  // Widths are pixels, never percentages.
  //
  // A percentage `<col>` width resolves against the table's own width, and this
  // table is `width: 100%` with a `min-width` floor — a circular basis that
  // Chromium resolves to zero. `calc(20% - 62px)` then collapses the column
  // entirely and the last one absorbs the slack, which is how four columns
  // vanished from a table that was rendering all seven `<th>` elements.
  //
  // Before the container has been measured, fall back to each column's floor:
  // the first paint is then slightly narrow rather than collapsed.
  const slack = containerWidth == null ? 0 : Math.max(0, containerWidth - fixedTotal);
  const widths = cols.map((c) => {
    if (c.width != null) return `${c.width}px`;
    if (containerWidth == null) return `${floorOf(c)}px`;
    const share = (slack * (c.flex ?? 1)) / flexTotal;
    return `${Math.max(floorOf(c), Math.round(share))}px`;
  });
  return { widths, minTableWidth };
};

export const StructureTable: React.FC<StructureTableProps> = ({
  data,
  columns,
  sortModel,
  onSortChange,
  defaultSortField,
  getDetailHref,
  rowActions,
  onRowAction,
  onColumnReorder,
  onColumnResize,
  loading,
  ariaLabel = 'Structure table',
  itemLabel = 'node',
  onRowOpen,
}) => {
  const visibleColumns = useMemo(() => columns.filter((c) => c.visible), [columns]);

  // When every visible column has an explicit width the table renders as a
  // fixed-pixel colgroup, so resizing one column never disturbs the others and
  // the table scrolls horizontally instead of squeezing.
  // Only freeze to the fixed-pixel model on pages that actually support resizing
  // (they pass onColumnResize). Consumers without it keep the flex/fill layout
  // even if their column defs happen to carry explicit widths.
  const isFrozen =
    !!onColumnResize && visibleColumns.length > 0 && visibleColumns.every((c) => c.width != null);
  const frozenTableWidth = useMemo(() => {
    if (!isFrozen) return undefined;
    const dataWidth = visibleColumns.reduce((sum, c) => sum + (c.width ?? 0), 0);
    return dataWidth + ACTIONS_COL_WIDTH;
  }, [isFrozen, visibleColumns]);

  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);

  // Flex/fill widths for the non-frozen state.
  const { widths: flexColWidths, minTableWidth } = useMemo(
    () => buildColLayout(visibleColumns, containerWidth),
    [visibleColumns, containerWidth]
  );

  // Seed every not-yet-sized column to its rendered content width immediately
  // after the first paint, so the table is ALWAYS in the precise fixed-pixel
  // model. Auto-layout can't shrink a column below its content, which is what
  // made dragging drift and columns un-shrinkable.
  const tableRef = useRef<HTMLTableElement>(null);
  useFreezeColumnWidths({ tableRef, isFrozen, ready: data.length > 0, onColumnResize });

  const handleSort = useCallback(
    (field: string) => {
      if (sortModel.field !== field) {
        onSortChange({ field, direction: 'asc' });
      } else if (sortModel.direction === 'asc') {
        onSortChange({ field, direction: 'desc' });
      } else {
        onSortChange({ field: defaultSortField, direction: 'asc' });
      }
    },
    [sortModel, onSortChange, defaultSortField]
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={32} sx={{ color: 'v4.accent' }} />
        </Box>
      )}
      <TableContainer>
        <Table
          ref={tableRef}
          size="small"
          aria-label={ariaLabel}
          sx={
            isFrozen
              ? // Every column has an explicit width → fixed-pixel model: resizing
                // one column leaves the rest untouched and the table scrolls
                // horizontally instead of squeezing.
                {
                  tableLayout: 'fixed',
                  width: frozenTableWidth,
                  // The frozen width is the sum of the measured column widths and
                  // is routinely narrower than the container — that gap is the
                  // white band beside the sticky actions column. Floor the table
                  // at the container width; the elastic last data column below
                  // absorbs the slack so every other column keeps its width.
                  minWidth: '100%',
                  ...stickyActionsTableSx,
                  ...elasticLastColumnSx,
                }
              : // Otherwise fill the container, distributing slack by flex weight
                // with minWidth floors (never squeeze below a legible minimum).
                {
                  width: '100%',
                  minWidth: minTableWidth,
                  tableLayout: 'fixed',
                  // No `elasticLastColumnSx` here: the colgroup below sizes every
                  // column, so the table already fills its container, and the
                  // stretch would beat the percentage `calc()` widths and collapse
                  // every flex column to 0px whenever one of them sorts last.
                  ...stickyActionsTableSx,
                }
          }
        >
          <colgroup>
            {visibleColumns.map((col, i) => {
              // While frozen, the last data column is left elastic (no width) so
              // it absorbs any slack when the frozen width is under the container
              // width. Under `table-layout: fixed` a width-less column takes the
              // remainder; sizing them all would stretch every column instead and
              // undo the widths the user dragged.
              const isElastic = isFrozen && i === visibleColumns.length - 1;
              return (
                <col
                  key={col.key}
                  style={isElastic ? undefined : { width: isFrozen ? col.width : flexColWidths[i] }}
                />
              );
            })}
            <col style={{ width: ACTIONS_COL_WIDTH }} />
          </colgroup>
          <StructureTableHead
            columns={visibleColumns}
            sortModel={sortModel}
            onSort={handleSort}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            // The last data column is elastic while frozen, so it has no width
            // of its own to drag — suppress its resize handle.
            elasticLastColumn={isFrozen}
          />
          <TableBody>
            {data.map((row, idx) => {
              const id = row.id as number;
              return (
                <StructureTableRow
                  key={id ?? idx}
                  row={row}
                  columns={visibleColumns}
                  even={idx % 2 === 1}
                  detailHref={getDetailHref(row)}
                  rowActions={rowActions}
                  onAction={(action) => onRowAction(id, action)}
                  itemLabel={itemLabel}
                  onOpen={onRowOpen}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
